import React from 'react';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * Panel component displaying the external accounting contact linkage status
 * for customers involved in the selected progress claims.
 *
 * @param {Object} props
 * @param {Array<{ id: string|number, name: string }>} props.uniqueCustomers - Distinct customer entities from claims
 * @param {Object} props.customersSyncStatus - Per-customer sync and linkage state map
 * @param {boolean} props.checkingCustomerLink - Whether customer links are being checked
 * @param {Object} props.selectedConnection - Active platform connection
 * @param {Function} props.handleSyncCustomer - Trigger function to push/link a customer
 * @param {boolean} props.syncing - Global sync operation state
 * @returns {React.ReactElement|null}
 */
export default function CustomerSyncStatusPanel({
    uniqueCustomers,
    customersSyncStatus,
    checkingCustomerLink,
    selectedConnection,
    handleSyncCustomer,
    syncing
}) {
    if (uniqueCustomers.length === 0) return null;

    const platformLabel = selectedConnection?.platform === 'xero' ? 'Xero' : selectedConnection?.platform === 'quickbooks' ? 'QBO' : 'MYOB';
    return (
        <div className="border rounded p-2 bg-light d-flex flex-column gap-1 project-select-card scroll-container customer-sync-scroll">
            <span className="font-weight-bold text-muted small text-uppercase project-select-title">Customer Sync Status</span>
            {uniqueCustomers.map(cust => {
                const status = customersSyncStatus[cust.id];
                const isLinked = status?.isLinked;
                const isSyncing = status?.syncing;
                return (
                    <div key={cust.id} className="d-flex justify-content-between align-items-center w-100 py-1 border-bottom border-light last-child-border-0">
                        <div className="fw-bold text-truncate customer-select-name" title={cust.name}>
                            {cust.name}
                        </div>
                        <div className="ms-2 d-flex align-items-center">
                            {checkingCustomerLink ? (
                                <Spinner size="sm" animation="border" className="text-secondary" />
                            ) : isLinked ? (
                                <span className="badge bg-success customer-sync-badge d-inline-flex align-items-center">
                                    <CheckCircleIcon className="me-1 icon-small" /> Linked ({platformLabel})
                                </span>
                            ) : (
                                <div className="d-flex align-items-center gap-1">
                                    <span className="badge bg-warning text-dark customer-sync-badge">Not Linked</span>
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => handleSyncCustomer(cust.id)}
                                        disabled={isSyncing || syncing}
                                        className="customer-sync-btn py-0 px-2 d-flex align-items-center justify-content-center"
                                        data-cy={`sync-invoices-sync-customer-btn-${cust.id}`}
                                    >
                                        {isSyncing ? 'Syncing...' : 'Sync'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
